import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";

export default function SpaceBookingSuccess() {
  const [params] = useSearchParams();
  const bookingId = params.get("bookingId");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [booking, setBooking] = useState(null);

  const fetchBooking = async () => {
    if (!bookingId) {
      setError("Missing bookingId");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      // Try to include related slot and space details if available
      const { data, error: err } = await supabase
        .from("bookings")
        .select(
          "id,type,status,total_amount_cents,currency,created_at,space_slots:space_slot_id(start_at,end_at,studio_spaces:space_id(name))"
        )
        .eq("id", bookingId)
        .single();
      if (err) throw err;
      setBooking(data || null);
      setError(null);
    } catch (e) {
      console.warn("[SpaceBookingSuccess] fetch error", e);
      setError("Could not load booking");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      await fetchBooking();
      // Light polling to catch webhook update
      const start = Date.now();
      const poll = async () => {
        if (!mounted) return;
        if (Date.now() - start > 20000) return; // stop after ~20s
        const { data } = await supabase
          .from("bookings")
          .select("status,total_amount_cents,currency,space_slots:space_slot_id(start_at,end_at,studio_spaces:space_id(name))")
          .eq("id", bookingId)
          .single();
        if (!mounted) return;
        if (data) setBooking((prev) => ({ ...(prev || {}), ...data }));
        if (data?.status === "paid" || data?.status === "confirmed") return;
        setTimeout(poll, 2500);
      };
      setTimeout(poll, 2500);
    };
    run();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const isConfirmed = booking?.status === "paid" || booking?.status === "confirmed";
  const wrongType = booking && booking.type && booking.type !== "space";
  const slot = booking?.space_slots || null;
  const whenStart = slot?.start_at ? new Date(slot.start_at) : null;
  const whenEnd = slot?.end_at ? new Date(slot.end_at) : null;
  const spaceName = slot?.studio_spaces?.name || null;

  return (
    <div className="min-h-[70vh] flex items-center justify-center text-white">
      <div className="p-6 rounded-xl bg-white/5 border border-white/10 text-center max-w-lg mx-4">
        {loading ? (
          <div>
            <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <div className="text-lg font-semibold">Checking your booking…</div>
            <div className="text-sm opacity-80 mt-1">This may take a few seconds.</div>
          </div>
        ) : error ? (
          <div>
            <div className="text-lg font-semibold mb-1">We couldn’t find this booking.</div>
            <div className="text-sm opacity-80 mb-4">Please contact the studio if you need help.</div>
            <div className="mt-4">
              <Link
                to="/discover/bookings"
                className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white"
              >
                Back to studio spaces
              </Link>
            </div>
          </div>
        ) : booking ? (
          <div>
            {wrongType ? (
              <div className="text-xs mb-3 opacity-70">Note: This booking is type “{booking.type}”.</div>
            ) : null}

            {isConfirmed ? (
              <>
                <div className="text-xl font-semibold mb-2">Studio space booking confirmed</div>
                {(spaceName || whenStart) ? (
                  <div className="text-sm opacity-90 mb-2">
                    {spaceName ? `${spaceName}` : "Space"}
                    {whenStart ? ` • ${whenStart.toLocaleString()}` : ""}
                    {whenEnd ? ` – ${whenEnd.toLocaleTimeString()}` : ""}
                  </div>
                ) : null}
                <div className="text-sm opacity-80">Enjoy your time in the studio.</div>
                <div className="mt-5">
                  <Link
                    to="/discover/bookings"
                    className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    Explore more spaces
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="text-xl font-semibold mb-2">Payment received</div>
                <div className="text-sm opacity-80">
                  We’re still finalising your booking — please refresh or check back shortly.
                </div>
                <div className="mt-5 flex items-center justify-center gap-3">
                  <button
                    className="px-4 py-2 rounded border border-white/15 text-white/90"
                    onClick={() => fetchBooking()}
                  >
                    Refresh now
                  </button>
                  <Link
                    to="/discover/bookings"
                    className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    Back to studio spaces
                  </Link>
                </div>
              </>
            )}
          </div>
        ) : (
          <div>
            <div className="text-lg font-semibold mb-1">We couldn’t find this booking.</div>
            <div className="text-sm opacity-80 mb-4">Please contact the studio if you need help.</div>
            <div className="mt-4">
              <Link
                to="/discover/bookings"
                className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white"
              >
                Back to studio spaces
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
